// The flat log table, the "Rålogg" tab. Feiloversikt only shows
// warn/error grouped into signatures, so this is where a single request gets
// followed through its info/debug breadcrumbs — click any Forespørsel id, or
// paste one into the filter, to pull every line that request produced. The
// request id lives in the URL so the Feiloversikt detail panel can link here.
import { useState } from "react";
import { useSearchParams } from "react-router";
import { Loader2, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useSystemLogs, useLogStats, useCleanupLogs } from "@/hooks/api";

const PAGE_SIZE = 100;

function levelBadge(level: string) {
  const variant =
    level === "error" ? "destructive" as const :
    level === "warn" ? "secondary" as const :
    "outline" as const;
  return <Badge variant={variant}>{level}</Badge>;
}

function LogDetailDialog({ log }: { log: any }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-left text-xs max-w-[400px] truncate hover:text-admin-primary cursor-pointer">{log.message}</button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
        <DialogHeader><DialogTitle>Loggdetalj</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-admin-text-secondary">Niveau:</span> {levelBadge(log.level)}</div>
            <div><span className="text-admin-text-secondary">Kontekst:</span> {log.context || "-"}</div>
            <div><span className="text-admin-text-secondary">Tidspunkt:</span> {new Date(log.created_at).toLocaleString("nb-NO")}</div>
            {log.request_id && <div><span className="text-admin-text-secondary">Forespørsel:</span> <code className="font-mono text-xs">{log.request_id}</code></div>}
            {log.job_id && <div><span className="text-admin-text-secondary">Jobb ID:</span> #{log.job_id}</div>}
            {log.duration != null && <div><span className="text-admin-text-secondary">Varighet:</span> {log.duration}ms</div>}
          </div>
          <div>
            <p className="text-admin-text-secondary mb-1">Melding:</p>
            <pre className="text-xs bg-admin-bg rounded p-2 whitespace-pre-wrap">{log.message}</pre>
          </div>
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <p className="text-admin-text-secondary mb-1">Metadata:</p>
              <pre className="text-xs bg-admin-bg rounded p-2 whitespace-pre-wrap">{JSON.stringify(log.metadata, null, 2)}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RawLogs() {
  const [page, setPage] = useState(0);
  const [levelFilter, setLevelFilter] = useState("");
  const [contextFilter, setContextFilter] = useState("");
  const [search, setSearch] = useState("");
  const [daysToKeep, setDaysToKeep] = useState(2);

  const [params, setParams] = useSearchParams();
  const requestId = params.get("requestId") ?? "";
  const setRequestId = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set("requestId", id);
    else next.delete("requestId");
    setParams(next, { replace: true });
    setPage(0);
  };

  const logStats = useLogStats();
  const logs = useSystemLogs({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    level: levelFilter || undefined,
    context: contextFilter || undefined,
    search: search || undefined,
    requestId: requestId || undefined,
  });
  const cleanupLogs = useCleanupLogs();

  const statsData = logStats.data as any;
  const contexts: { context: string; count: number }[] = statsData?.contexts ?? [];

  return (
    <div className="space-y-4 mt-4">
      {statsData && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-admin-text-secondary">Totalt</p>
              <p className="text-2xl font-bold">{statsData.total?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-admin-text-secondary">Debug</p>
              <p className="text-2xl font-bold text-admin-text-secondary">{statsData.debug?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-admin-text-secondary">Info</p>
              <p className="text-2xl font-bold text-admin-info-400">{statsData.info?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-admin-text-secondary">Warn</p>
              <p className="text-2xl font-bold text-admin-caution-400">{statsData.warn?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-admin-text-secondary">Error</p>
              <p className="text-2xl font-bold text-admin-danger-400">{statsData.error?.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {requestId && (
        <div className="flex items-center gap-3 rounded border border-admin-primary/40 bg-admin-primary-active px-3 py-2 text-sm">
          <span className="text-admin-text-secondary">
            Viser alle linjer for forespørsel
          </span>
          <code className="font-mono text-admin-primary">{requestId}</code>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => setRequestId("")}
          >
            Vis alle igjen
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="bg-admin-bg border border-admin-card-border rounded px-3 py-1.5 text-sm"
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value); setPage(0); }}
        >
          <option value="">Alle nivaer</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>

        <select
          className="bg-admin-bg border border-admin-card-border rounded px-3 py-1.5 text-sm"
          value={contextFilter}
          onChange={(e) => { setContextFilter(e.target.value); setPage(0); }}
        >
          <option value="">Alle kontekster</option>
          {contexts.map((c) => <option key={c.context} value={c.context}>{c.context} ({c.count})</option>)}
        </select>

        <Input
          placeholder="Sok i meldinger..."
          className="max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") setPage(0); }}
        />

        <Input
          placeholder="Forespørsel-ID..."
          className="max-w-[180px] font-mono text-xs"
          value={requestId}
          onChange={(e) => setRequestId(e.target.value.trim())}
        />

        <div className="ml-auto flex gap-2 items-center">
          <Input
            type="number"
            min={1}
            className="w-20"
            value={daysToKeep}
            onChange={(e) => setDaysToKeep(parseInt(e.target.value) || 7)}
          />
          <span className="text-xs text-admin-text-secondary">dager</span>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const result = await cleanupLogs.mutateAsync(daysToKeep);
                toast.success(`${(result as any)?.deletedCount ?? 0} logger slettet`);
              } catch (err: any) {
                toast.error(err.message || "Feil");
              }
            }}
            disabled={cleanupLogs.isPending}
          >
            <Trash2 className="h-3 w-3 mr-1" /> Rydd opp
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          {logs.isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tidspunkt</TableHead>
                    <TableHead>Niva</TableHead>
                    <TableHead>Kontekst</TableHead>
                    <TableHead>Melding</TableHead>
                    <TableHead>Forespørsel</TableHead>
                    <TableHead>Jobb ID</TableHead>
                    <TableHead>Varighet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(logs.data ?? []).map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString("nb-NO", { hour: "2-digit", minute: "2-digit", second: "2-digit", day: "2-digit", month: "short" })}</TableCell>
                      <TableCell>{levelBadge(log.level)}</TableCell>
                      <TableCell className="text-xs font-mono">{log.context || "-"}</TableCell>
                      <TableCell><LogDetailDialog log={log} /></TableCell>
                      <TableCell className="text-xs font-mono">
                        {log.request_id ? (
                          <button
                            className="text-admin-primary hover:underline cursor-pointer"
                            title="Vis alle linjer for denne forespørselen"
                            onClick={() => setRequestId(log.request_id)}
                          >
                            {log.request_id}
                          </button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{log.job_id ? `#${log.job_id}` : "-"}</TableCell>
                      <TableCell className="text-xs">{log.duration != null ? `${log.duration}ms` : "-"}</TableCell>
                    </TableRow>
                  ))}
                  {(logs.data ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-admin-text-secondary py-8">Ingen logger funnet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-admin-text-secondary">
                  Side {page + 1} — Viser {(logs.data ?? []).length} logger
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={(logs.data ?? []).length < PAGE_SIZE} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
